<template>
  <b-container fluid>
    <!-- Header -->
    <b-row class="mb-3">
      <b-col class="px-sm-3 px-0 d-flex align-items-center justify-content-between">
        <h1 class="mb-0">{{ network.name || 'Network' }}</h1>
        <b-button variant="outline-secondary" size="sm" @click="$router.push('/networks')">
          &larr; Back to Networks
        </b-button>
      </b-col>
    </b-row>

    <error-alert :errors="errors" />

    <!-- Network Info Summary -->
    <b-row class="mb-4">
      <b-col sm="6" md="3">
        <small class="text-muted d-block">Hostname</small>
        <span>{{ network.hostname || '-' }}</span>
      </b-col>
      <b-col sm="6" md="3">
        <small class="text-muted d-block">Type</small>
        <span>{{ network.fulfillment_type | capitalize }}</span>
      </b-col>
      <b-col sm="6" md="3">
        <small class="text-muted d-block">Status</small>
        <span>
          <span
            class="status-dot"
            :class="network.status === 'active' ? 'status-active' : 'status-inactive'"
          ></span>
          {{ network.status | capitalize }}
        </span>
      </b-col>
      <b-col sm="6" md="3">
        <small class="text-muted d-block">Company ID</small>
        <span>{{ network.company_id || '-' }}</span>
      </b-col>
    </b-row>

    <template v-if="brandFilteringEnabled">
      <hr />

      <!-- Brand Filters Section -->
      <b-row class="mb-3">
        <b-col class="px-sm-3 px-0">
          <h4>Brand Filters</h4>
          <p class="text-muted mb-3">
            Manage which brands are filtered on this network by date range. Click a date on the
            calendar to see active filters, or add a new filter below.
          </p>
        </b-col>
      </b-row>

      <b-row>
        <!-- Calendar -->
        <b-col md="7" lg="8" class="mb-4">
          <div class="calendar-container">
            <v-calendar
              :attributes="calendarAttributes"
              :columns="calendarColumns"
              is-expanded
              @dayclick="onDayClick"
            />
          </div>

          <!-- Selected Date Info -->
          <div v-if="selectedDate" class="mt-3 selected-date-panel">
            <h6>
              Filters active on {{ formatSelectedDate }}
            </h6>
            <div v-if="filtersOnSelectedDate.length === 0" class="text-muted">
              No brand filters active on this date.
            </div>
            <b-list-group v-else flush>
              <b-list-group-item
                v-for="filter in filtersOnSelectedDate"
                :key="filter.id"
                class="d-flex justify-content-between align-items-center py-2"
              >
                <div>
                  <strong>{{ getBrandName(filter.brand_id) }}</strong>
                  <small class="text-muted d-block">
                    {{ formatFilterDate(filter.starts_at) }} &mdash; {{ formatFilterDate(filter.ends_at) }}
                  </small>
                </div>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="removeFilter(filter)"
                  :disabled="deleting === filter.id"
                >
                  <span v-if="deleting === filter.id">Removing...</span>
                  <span v-else>Remove</span>
                </b-button>
              </b-list-group-item>
            </b-list-group>
          </div>
        </b-col>

        <!-- Add Filter Panel -->
        <b-col md="5" lg="4" class="mb-4">
          <b-card header="Add Brand Filter" class="add-filter-card">
            <b-form @submit.prevent="addFilter">
              <b-form-group label="Brand" label-size="sm">
                <multiselect
                  v-model="newFilter.brand"
                  :options="brands"
                  :searchable="true"
                  :loading="loadingBrands"
                  label="name"
                  track-by="id"
                  placeholder="Search for a brand..."
                  @search-change="searchBrands"
                />
              </b-form-group>

              <b-form-group label="Start Date" label-size="sm">
                <v-date-picker v-model="newFilter.starts_at" :popover="{ placement: 'bottom' }">
                  <template #default="{ inputValue, inputEvents }">
                    <b-form-input
                      :value="inputValue"
                      v-on="inputEvents"
                      placeholder="Select start date"
                      size="sm"
                      readonly
                    />
                  </template>
                </v-date-picker>
              </b-form-group>

              <b-form-group label="End Date" label-size="sm">
                <v-date-picker
                  v-model="newFilter.ends_at"
                  :min-date="newFilter.starts_at"
                  :popover="{ placement: 'bottom' }"
                >
                  <template #default="{ inputValue, inputEvents }">
                    <b-form-input
                      :value="inputValue"
                      v-on="inputEvents"
                      placeholder="Select end date"
                      size="sm"
                      readonly
                    />
                  </template>
                </v-date-picker>
              </b-form-group>

              <b-button
                type="submit"
                variant="primary"
                block
                size="sm"
                :disabled="!canAddFilter || saving"
              >
                {{ saving ? 'Adding...' : 'Add Filter' }}
              </b-button>
            </b-form>
          </b-card>

          <!-- Existing Filters List -->
          <b-card header="All Active Filters" class="mt-3 filters-list-card">
            <div v-if="loading" class="text-center py-3">
              <b-spinner small />
            </div>
            <div v-else-if="filters.length === 0" class="text-muted text-center py-3">
              No brand filters configured.
            </div>
            <b-list-group v-else flush>
              <b-list-group-item
                v-for="filter in filters"
                :key="filter.id"
                class="d-flex justify-content-between align-items-center px-0 py-2"
                :class="{ 'filter-past': isFilterPast(filter) }"
              >
                <div>
                  <strong>{{ getBrandName(filter.brand_id) }}</strong>
                  <small class="text-muted d-block">
                    {{ formatFilterDate(filter.starts_at) }} &mdash; {{ formatFilterDate(filter.ends_at) }}
                  </small>
                </div>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="removeFilter(filter)"
                  :disabled="deleting === filter.id"
                >
                  &times;
                </b-button>
              </b-list-group-item>
            </b-list-group>
          </b-card>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import ResourceAPI from '../../services/ResourceAPI';
import NetworkBrandFiltersAPI from '../../services/NetworkBrandFiltersAPI';

export default {
  name: 'Network',
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      network: {},
      filters: [],
      brands: [],
      brandMap: {},
      loading: false,
      loadingBrands: false,
      saving: false,
      deleting: null,
      errors: [],
      selectedDate: null,
      newFilter: {
        brand: null,
        starts_at: null,
        ends_at: null,
      },
      brandSearchTimeout: null,
    };
  },
  computed: {
    ...mapGetters('session', ['organizationAdmin', 'organizationIds']),
    brandFilteringEnabled() {
      return this.network.options && this.network.options.brand_filtering_enabled;
    },
    calendarColumns() {
      return this.$screens({ default: 1, md: 2 });
    },
    canAddFilter() {
      return this.newFilter.brand && this.newFilter.starts_at && this.newFilter.ends_at;
    },
    formatSelectedDate() {
      if (!this.selectedDate) return '';
      return this.selectedDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    filtersOnSelectedDate() {
      if (!this.selectedDate) return [];
      const selected = this.normalizeDate(this.selectedDate);
      return this.filters.filter((f) => {
        const start = this.normalizeDate(new Date(f.starts_at));
        const end = this.normalizeDate(new Date(f.ends_at));
        return selected >= start && selected <= end;
      });
    },
    calendarAttributes() {
      const attrs = [];

      // Highlight date ranges for each filter with distinct colors
      const colors = ['blue', 'green', 'purple', 'orange', 'red', 'teal', 'yellow', 'pink'];
      this.filters.forEach((filter, index) => {
        const color = colors[index % colors.length];
        attrs.push({
          key: `filter-${filter.id}`,
          highlight: {
            color,
            fillMode: 'light',
          },
          dates: {
            start: new Date(filter.starts_at),
            end: new Date(filter.ends_at),
          },
          popover: {
            label: this.getBrandName(filter.brand_id),
          },
        });
      });

      // Selected date indicator
      if (this.selectedDate) {
        attrs.push({
          key: 'selected',
          highlight: {
            color: 'gray',
            fillMode: 'outline',
          },
          dates: this.selectedDate,
        });
      }

      return attrs;
    },
  },
  watch: {
    brandFilteringEnabled(enabled) {
      if (enabled) {
        this.loadFilters();
        this.searchBrands('');
      }
    },
  },
  created() {
    if (!this.organizationAdmin) {
      this.$router.push('/');
      return;
    }
    this.loadNetwork();
  },
  methods: {
    async loadNetwork() {
      try {
        const orgId = this.$route.query.organization_id || this.organizationIds[0];
        const response = await ResourceAPI.getRecords(
          `organizations/${orgId}/networks`,
          { filters: { id: this.id } }
        );
        const networks = response.data || response;
        if (Array.isArray(networks) && networks.length > 0) {
          [this.network] = networks;
        }
      } catch (error) {
        this.errors = ['Failed to load network details.'];
      }
    },
    async loadFilters() {
      this.loading = true;
      try {
        const response = await NetworkBrandFiltersAPI.getFilters(this.id, {
          with: 'brand',
          limit: 100,
        });
        this.filters = response.data || response || [];
        // Build brand map from loaded filters
        this.filters.forEach((f) => {
          if (f.brand) {
            this.brandMap[f.brand_id] = f.brand.name;
          }
        });
      } catch (error) {
        this.errors = ['Failed to load brand filters.'];
      } finally {
        this.loading = false;
      }
    },
    async searchBrands(query) {
      clearTimeout(this.brandSearchTimeout);
      this.brandSearchTimeout = setTimeout(async () => {
        this.loadingBrands = true;
        try {
          const params = { limit: 50, network_id: this.id };
          if (query) {
            params.filters = { name: query };
          }
          const response = await ResourceAPI.getRecords('brands', params);
          this.brands = response.data || response || [];
          // Update brand map
          this.brands.forEach((b) => {
            this.brandMap[b.id] = b.name;
          });
        } catch (error) {
          this.brands = [];
        } finally {
          this.loadingBrands = false;
        }
      }, 300);
    },
    async addFilter() {
      if (!this.canAddFilter) return;
      this.saving = true;
      this.errors = [];
      try {
        await NetworkBrandFiltersAPI.createFilter(this.id, {
          brand_id: this.newFilter.brand.id,
          starts_at: this.formatDateForAPI(this.newFilter.starts_at),
          ends_at: this.formatDateForAPI(this.newFilter.ends_at),
        });
        // Store brand name before resetting
        this.brandMap[this.newFilter.brand.id] = this.newFilter.brand.name;
        this.newFilter = { brand: null, starts_at: null, ends_at: null };
        await this.loadFilters();
      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to add brand filter.';
        this.errors = [msg];
      } finally {
        this.saving = false;
      }
    },
    async removeFilter(filter) {
      this.deleting = filter.id;
      this.errors = [];
      try {
        await NetworkBrandFiltersAPI.deleteFilter(this.id, filter.id);
        await this.loadFilters();
      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to remove brand filter.';
        this.errors = [msg];
      } finally {
        this.deleting = null;
      }
    },
    onDayClick(day) {
      this.selectedDate = day.date;
    },
    getBrandName(brandId) {
      return this.brandMap[brandId] || `Brand #${brandId}`;
    },
    formatFilterDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    formatDateForAPI(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    normalizeDate(date) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    },
    isFilterPast(filter) {
      return this.normalizeDate(new Date(filter.ends_at)) < this.normalizeDate(new Date());
    },
  },
};
</script>

<style scoped>
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}
.status-active {
  background-color: #28a745;
}
.status-inactive {
  background-color: #dc3545;
}

.calendar-container {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  background: #fff;
}

.selected-date-panel {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  background: #f8f9fa;
}

.add-filter-card .card-header,
.filters-list-card .card-header {
  font-weight: 600;
  font-size: 0.9rem;
}

.filter-past {
  opacity: 0.5;
}

.filters-list-card .list-group-item {
  border-left: 0;
  border-right: 0;
}
.filters-list-card .list-group-item:first-child {
  border-top: 0;
}
.filters-list-card .list-group-item:last-child {
  border-bottom: 0;
}
</style>
